/**
 * DeliveryTrackingMap
 *
 * Embeddable Google Maps component for delivery tracking.
 * Renders:
 *  - A departure marker (green) at the "In Transit" ping location
 *  - An arrival marker (checkered flag) at the "Delivered" ping location
 *  - The encoded polyline route between them
 *  - An ETA info panel overlaid on the map
 *
 * Props:
 *  route        – delivery_routes row (from GET /api/delivery/shipments/:id/route)
 *  isDark       – theme flag
 *  height       – CSS height string (default '320px')
 */

import { useEffect, useRef, useState } from 'react';
import { Navigation, Clock, MapPin, CheckCircle2, Loader } from 'lucide-react';

// Reuse the same singleton loader as GoogleMapView so the script is only injected once
let googleMapsLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google && window.google.maps) return Promise.resolve();
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;
  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const keyParam = apiKey ? `&key=${apiKey}` : '';
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=marker,places,geometry${keyParam}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

function formatDuration(seconds) {
  if (!seconds) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

function formatDistance(meters) {
  if (!meters) return null;
  if (meters >= 1609) {
    return `${(meters / 1609.34).toFixed(1)} mi`;
  }
  return `${meters} m`;
}

export default function DeliveryTrackingMap({ route, isDark, height = '320px', showBothLocations = false, actualLocation = null, intendedLocation = null }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Load Google Maps once
  useEffect(() => {
    loadGoogleMaps(apiKey)
      .then(() => setMapReady(true))
      .catch(err => {
        console.error('Failed to load Google Maps:', err);
        setMapError('Failed to load map');
      });
  }, [apiKey]);

  // Initialize map
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;

    const defaultCenter = route?.origin_lat
      ? { lat: parseFloat(route.origin_lat), lng: parseFloat(route.origin_lng) }
      : { lat: 39.5, lng: -98.35 }; // center of US as fallback

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 10,
      mapTypeId: 'roadmap',
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true,
      styles: isDark ? DARK_MAP_STYLES : [],
    });
  }, [mapReady, isDark, route]);

  // Draw route whenever route data changes
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !route) return;

    const map = mapInstanceRef.current;

    // Clear old markers and polyline
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    const bounds = new window.google.maps.LatLngBounds();

    // ── Special mode: Show both intended and actual delivery locations ─────
    if (showBothLocations && intendedLocation && actualLocation) {
      // Intended location (green)
      const intendedPos = {
        lat: parseFloat(intendedLocation.lat),
        lng: parseFloat(intendedLocation.lng),
      };
      const intendedMarker = new window.google.maps.Marker({
        position: intendedPos,
        map,
        title: 'Intended Delivery Location',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#22c55e',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        label: {
          text: '✓',
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: 'bold',
        },
        zIndex: 10,
      });
      const intendedInfoWindow = new window.google.maps.InfoWindow({
        content: `<div style="font-family:sans-serif;font-size:13px;padding:4px 2px">
          <strong style="color:#16a34a">Intended Delivery Location</strong><br/>
          ${route.customer_address || `${intendedLocation.lat}, ${intendedLocation.lng}`}
        </div>`,
      });
      intendedMarker.addListener('click', () => intendedInfoWindow.open(map, intendedMarker));
      markersRef.current.push(intendedMarker);
      bounds.extend(intendedPos);

      // Actual delivery location (red)
      const actualPos = {
        lat: parseFloat(actualLocation.lat),
        lng: parseFloat(actualLocation.lng),
      };
      const actualMarker = new window.google.maps.Marker({
        position: actualPos,
        map,
        title: 'Actual Delivery Location',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#ef4444',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        label: {
          text: '✗',
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: 'bold',
        },
        zIndex: 10,
      });
      const actualInfoWindow = new window.google.maps.InfoWindow({
        content: `<div style="font-family:sans-serif;font-size:13px;padding:4px 2px">
          <strong style="color:#dc2626">Actual Delivery Location</strong><br/>
          ${route.destination_address || `${actualLocation.lat}, ${actualLocation.lng}`}
        </div>`,
      });
      actualMarker.addListener('click', () => actualInfoWindow.open(map, actualMarker));
      markersRef.current.push(actualMarker);
      bounds.extend(actualPos);

      // Draw line between them
      const line = new window.google.maps.Polyline({
        path: [intendedPos, actualPos],
        geodesic: true,
        strokeColor: '#f59e0b',
        strokeOpacity: 0.7,
        strokeWeight: 3,
        map,
      });
      polylineRef.current = line;
    } else {
      // ── Normal mode: Departure marker (green pin) ────────────────────────
      if (route.origin_lat && route.origin_lng) {
        const originPos = {
          lat: parseFloat(route.origin_lat),
          lng: parseFloat(route.origin_lng),
        };
        const departureMarker = new window.google.maps.Marker({
          position: originPos,
          map,
          title: 'Departure',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#22c55e',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          label: {
            text: 'D',
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: 'bold',
          },
          zIndex: 10,
        });

        const depInfoWindow = new window.google.maps.InfoWindow({
          content: `<div style="font-family:sans-serif;font-size:13px;padding:4px 2px">
            <strong style="color:#16a34a">Departure</strong><br/>
            ${route.origin_address || `${route.origin_lat}, ${route.origin_lng}`}
          </div>`,
        });
        departureMarker.addListener('click', () => depInfoWindow.open(map, departureMarker));
        markersRef.current.push(departureMarker);
        bounds.extend(originPos);
      }

      // ── Arrival marker (checkered / red pin) ─────────────────────────────
      if (route.destination_lat && route.destination_lng) {
        const destPos = {
          lat: parseFloat(route.destination_lat),
          lng: parseFloat(route.destination_lng),
        };
        const arrivalMarker = new window.google.maps.Marker({
          position: destPos,
          map,
          title: 'Delivered',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#ef4444',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          label: {
            text: 'A',
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: 'bold',
          },
          zIndex: 10,
        });

        const arrInfoWindow = new window.google.maps.InfoWindow({
          content: `<div style="font-family:sans-serif;font-size:13px;padding:4px 2px">
            <strong style="color:#dc2626">Delivered</strong><br/>
            ${route.destination_address || `${route.destination_lat}, ${route.destination_lng}`}
          </div>`,
        });
        arrivalMarker.addListener('click', () => arrInfoWindow.open(map, arrivalMarker));
        markersRef.current.push(arrivalMarker);
        bounds.extend(destPos);
      } else if (route.customer_address) {
        // If not yet delivered, show a dashed destination pin at the customer address
        // We can't geocode without an async call here, so we just note it in the ETA panel
      }

      // ── Polyline (only in normal mode) ─────────────────────────────────────
      if (route.route_polyline) {
        try {
          console.log('[DeliveryTrackingMap] Decoding polyline:', {
            polylineLength: route.route_polyline.length,
            shipmentId: route.shipment_id
          });
          const decodedPath = window.google.maps.geometry.encoding.decodePath(route.route_polyline);
          console.log('[DeliveryTrackingMap] Decoded polyline points:', decodedPath.length);
          polylineRef.current = new window.google.maps.Polyline({
            path: decodedPath,
            geodesic: true,
            strokeColor: '#7c3aed',
            strokeOpacity: 0.85,
            strokeWeight: 4,
            map,
          });
          decodedPath.forEach(pt => bounds.extend(pt));
        } catch (e) {
          console.error('[DeliveryTrackingMap] Could not decode route polyline:', e, {
            polyline: route.route_polyline?.substring(0, 50) + '...',
            shipmentId: route.shipment_id
          });
        }
      }
    }

    // ── Fit bounds ────────────────────────────────────────────────────────
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { top: 40, right: 20, bottom: 20, left: 20 });
    } else {
      console.warn('[DeliveryTrackingMap] No bounds to fit:', {
        shipmentId: route.shipment_id,
        routeStatus: route.status,
        hasOrigin: !!(route.origin_lat && route.origin_lng),
        hasDestination: !!(route.destination_lat && route.destination_lng),
        showBothLocations,
        hasIntended: !!(intendedLocation?.lat && intendedLocation?.lng),
        hasActual: !!(actualLocation?.lat && actualLocation?.lng)
      });
    }
  }, [mapReady, route, showBothLocations, actualLocation, intendedLocation, isDark]);

  if (mapError) {
    return (
      <div
        style={{ height }}
        className={`flex items-center justify-center rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'}`}
      >
        <p className="text-sm">{mapError}</p>
      </div>
    );
  }

  if (!route) {
    return (
      <div
        style={{ height }}
        className={`flex flex-col items-center justify-center rounded-xl border gap-2 ${isDark ? 'bg-gray-800/50 border-gray-700 text-gray-500' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
      >
        <Navigation size={28} className="opacity-40" />
        <p className="text-sm">No tracking data yet</p>
        <p className="text-xs opacity-60">Location will be captured when moved to In Transit</p>
      </div>
    );
  }

  const isDelivered = route.status === 'delivered';
  const isInTransit = route.status === 'in_transit';
  const eta = route.estimated_arrival_at ? new Date(route.estimated_arrival_at) : null;
  const now = new Date();
  const etaIsPast = eta && eta < now;

  return (
    <div className="relative rounded-xl overflow-hidden border" style={{ height }}
      {...(isDark ? { style: { height, borderColor: '#374151' } } : { style: { height, borderColor: '#e5e7eb' } })}
    >
      {/* Map canvas */}
      <div ref={mapRef} className="w-full h-full" />

      {/* Loading overlay */}
      {!mapReady && (
        <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <Loader size={24} className="animate-spin text-violet-500" />
        </div>
      )}

      {/* ETA / Status overlay */}
      <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
        <div className={`rounded-xl px-4 py-3 shadow-xl backdrop-blur-sm border flex flex-wrap items-center gap-x-5 gap-y-2 ${
          isDark ? 'bg-gray-900/80 border-gray-700' : 'bg-white/90 border-gray-200'
        }`}>

          {/* Status pill */}
          <div className="flex items-center gap-2">
            {isDelivered ? (
              <CheckCircle2 size={15} className="text-green-400 flex-shrink-0" />
            ) : (
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            )}
            <span className={`text-xs font-semibold ${isDelivered ? 'text-green-400' : 'text-amber-400'}`}>
              {isDelivered ? 'Delivered' : 'In Transit'}
            </span>
          </div>

          {/* ETA */}
          {eta && !isDelivered && (
            <div className="flex items-center gap-1.5">
              <Clock size={13} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                ETA: {etaIsPast ? 'Overdue' : eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {' '}({etaIsPast ? 'was ' : ''}{eta.toLocaleDateString()})
              </span>
            </div>
          )}

          {/* Actual arrival */}
          {isDelivered && route.actual_arrival_at && (
            <div className="flex items-center gap-1.5">
              <Clock size={13} className="text-green-400" />
              <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Arrived: {new Date(route.actual_arrival_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}

          {/* Distance */}
          {route.estimated_distance_meters && (
            <div className="flex items-center gap-1.5">
              <MapPin size={13} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {formatDistance(route.estimated_distance_meters)}
                {route.estimated_duration_seconds && ` · ${formatDuration(route.estimated_duration_seconds)}`}
              </span>
            </div>
          )}

          {/* Destination */}
          {route.customer_address && (
            <div className="flex items-center gap-1.5 min-w-0">
              <Navigation size={13} className="text-violet-400 flex-shrink-0" />
              <span className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {route.customer_address}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Dark mode map styles ──────────────────────────────────────────────────────
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1d2535' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1d2535' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a9bb0' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d3748' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a202c' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3d4f6b' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
];
